import React, {useEffect, useState} from "react";
import {Button, Col, Container, Form, Row, Table} from 'react-bootstrap';
import Pagination from "react-js-pagination";
import {useSelector} from "react-redux";
import partsService from "../../../services/parts.service";
import Toaster from "../../../common/ToastMsg";
import {ToastContainer} from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import {Navigate} from "react-router-dom";
import UserService from "../../../services/user.service";
import {formatNumber} from "../../../common/Utils";

const SoldHistory = () => {
    const [clients, setClients] = useState([]);
    const [clientsLoading, setClientsLoading] = useState(false);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);

    // State for input and validation
    const [days, setDays] = useState("30");
    const [isValid, setIsValid] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedOption, setSelectedOption] = useState("");
    const { user: currentUser } = useSelector((state) => state.auth);

    // State for summary data
    const [summary, setSummary] = useState(null);
    const [showDetails, setShowDetails] = useState(false);
    
    // State for details data with pagination
    const [details, setDetails] = useState([]);
    const [pageRequest, setPageRequest] = useState({ page: 0, size: 10 });
    const [totalPages, setTotalPages] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [displayRange, setDisplayRange] = useState('');

    // Update display range for pagination
    const updateDisplayRange = (items) => {
        const itemCount = items !== undefined ? items : totalItems;
        let start = pageRequest.page * pageRequest.size + 1;
        const end = Math.min(start + pageRequest.size - 1, itemCount);
        if (itemCount === 0) start = 0;
        setDisplayRange(`${start}-${end}`);
    };
    
    // Handle page change
    const handlePageChange = (pageNumber) => {
        setPageRequest(prevState => ({...prevState, page: pageNumber - 1}));
    };
    
    // Handle page size change
    const handlePageSizeChange = (event) => {
        const newSize = parseInt(event.target.value);
        setPageRequest(prevState => ({...prevState, size: newSize, page: 0}));
    };

    useEffect(() => {
        setClientsLoading(true);
        UserService.getClientList().then(
            (response) => {
                setClients(response.data);
                if (response.data instanceof Array && response.data.length > 0) {
                    setSelectedOption(response.data[0].oid);
                }
                setClientsLoading(false);
            },
            (error) => {
                console.log(error);
                Toaster.error({
                    title: "Failed to load clients !",
                    text: error.message
                }, {
                    position: "top-center",
                    containerId: "search"
                });
                setClientsLoading(false);
            }
        )

    }, []);
    
    // Effect to update display range when pagination changes
    useEffect(() => {
        if (showDetails) {
            fetchDetails();
        }
    }, [pageRequest.page, pageRequest.size]);
    
    // Get current page items for pagination
    const getCurrentPageItems = () => {
        // If the backend is already handling pagination, just return all details
        return details;
    };

    if (!currentUser) {
        return <Navigate to="/login" />;
    }

    const handleSelection = (event) => {
        setSelectedOption(event.target.value);
        reset();
    };


    const reset = () => {
        setSummary(null);
        setShowDetails(false);
        setDetails([]);
        setPageRequest({ page: 0, size: 10 });
        setTotalPages(0);
        setTotalItems(0);
        setDisplayRange('');
    }
    // Handle input change with validation
    const handleDaysChange = (e) => {
        const value = e.target.value;
        setDays(value);

        // Validate input is a positive integer
        if (value === "" || !/^\d+$/.test(value) || parseInt(value) <= 0) {
            setIsValid(false);
            setErrorMessage("Please enter a positive integer");
        } else {
            setIsValid(true);
            setErrorMessage("");
        }
    };
    
    // Handle summary query submission
    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!isValid || days === "") {
            return;
        }
        
        // Clear previous results
        reset();
        
        // Call API for summary data
        setSummaryLoading(true);
        partsService.soldHistorySummary(selectedOption, days)
            .then(response => {
                setSummary(response.data);
                setSummaryLoading(false);
            })
            .catch(error => {
                console.error("Error fetching summary:", error);
                Toaster.error({
                    title: "Failed to load summary!",
                    text: error.response?.data?.message || error.message
                }, {
                    position: "top-center",
                    containerId: "sold-history"
                });
                setSummaryLoading(false);
            });
    };
    
    // Handle details button click
    const handleDetailsClick = () => {
        setShowDetails(true);
        fetchDetails();
    };
    
    // Fetch details data with pagination
    const fetchDetails = () => {
        setDetailsLoading(true);
        let newPageRequest = {...pageRequest};
        newPageRequest.clientId = selectedOption;
        newPageRequest.days = days;
        partsService.soldHistoryDetails(newPageRequest)
            .then(response => {
                const data = response.data;
                
                let itemCount = 0;
                // If the response is already paginated
                if (data.totalPages !== undefined) {
                    setDetails(data.content || []); // Handle paginated response
                    setTotalPages(data.totalPages);
                    setTotalItems(data.totalElements);
                    itemCount = data.totalElements;
                } else {
                    // If the response is not paginated, we need to paginate it client-side
                    setDetails(data); // Store all data
                    itemCount = data.length;
                    setTotalItems(itemCount);
                    setTotalPages(Math.ceil(itemCount / pageRequest.size));
                }
                
                updateDisplayRange(itemCount);
                setDetailsLoading(false);
            })
            .catch(error => {
                console.error("Error fetching details:", error);
                Toaster.error({
                    title: "Failed to load details!",
                    text: error.response?.data?.message || error.message
                }, {
                    position: "top-center",
                    containerId: "sold-history"
                });
                setDetailsLoading(false);
            });
    };
    
    return (
        <Container fluid>
            <ToastContainer autoClose={3000} containerId={"sold-history"} />
            <Row>
                <Col xs={12} md={8}>
                    <div className="dropdown mb-2">
                        <select
                            value={selectedOption}
                            className="form-select"
                            onChange={handleSelection}
                            disabled={clientsLoading || summaryLoading || detailsLoading}
                        >
                            {!clients &&
                                <option value="" disabled>
                                    No client found
                                </option>
                            }

                            {clients && clients.map((client, index) => (
                                <option value={client.oid}>
                                    {client.clientName}
                                </option>
                            ))}

                        </select>
                        {clientsLoading && (
                            <span className="spinner-border spinner-border-sm text-primary mt-2" role="status"></span>
                        )}
                    </div>
                </Col>
            </Row>

            {/* Input Form */}
            <Row className="mb-4">
                <Col xs={12} md={6}>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm={4}>
                                Inventory sold in past
                            </Form.Label>
                            <Col sm={2}>
                                <Form.Control
                                    type="number"
                                    value={days}
                                    onChange={handleDaysChange}
                                    isInvalid={!isValid}
                                    min="1"
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errorMessage}
                                </Form.Control.Feedback>
                            </Col>
                            <Form.Label column sm={2}>
                                days
                            </Form.Label>
                        </Form.Group>
                        <Button variant="primary" type="submit" disabled={!isValid || days === "" || summaryLoading}>
                            {summaryLoading && <span className="spinner-border spinner-border-sm me-2" role="status"></span>}
                            {summaryLoading ? "Querying..." : "Query"}
                        </Button>
                    </Form>
                </Col>
            </Row>
            
            {/* Summary Section */}
            {summary && (
                <Row className="mb-4">
                    <Col>
                        <h4>Summary</h4>
                        <div className="p-3 border rounded">
                            <p><strong>Total Sold Quantity:</strong> {summary.qty}</p>
                            <p><strong>Total Sold Amount:</strong> {summary.total}</p>
                            
                            {summary.qty > 0 && (
                                <Button 
                                    variant="info" 
                                    onClick={handleDetailsClick}
                                    className="mt-2"
                                    disabled={detailsLoading}
                                >
                                    {detailsLoading && <span className="spinner-border spinner-border-sm me-2" role="status"></span>}
                                    {detailsLoading ? "Loading..." : "View Details"}
                                </Button>
                            )}
                        </div>
                    </Col>
                </Row>
            )}
            
            {/* Details Section with Pagination */}
            {showDetails && (
                <Row>
                    <Col>
                        <h4>Details</h4>
                        <div className="table-responsive">
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>Client</th>
                                        <th>Parts Number</th>
                                        <th>Description</th>
                                        <th>Quantity</th>
                                        <th>Amount</th>
                                        <th>Date</th>
                                        <th>Invoice Number</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {detailsLoading && (
                                        <tr>
                                            <td colSpan="7" className="text-center">
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Loading details...
                                            </td>
                                        </tr>
                                    )}
                                    {getCurrentPageItems().map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.client}</td>
                                            <td>{item.partsNumber}</td>
                                            <td>{item.description}</td>
                                            <td>{item.qty}</td>
                                            <td>${formatNumber(item.amount)}</td>
                                            <td>{item.date}</td>
                                            <td>{item.invoiceNumber}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                        
                        {/* Pagination Controls */}
                        <Row className="mt-3">
                            <Col className="d-flex justify-content-center">
                                <Pagination
                                    activePage={pageRequest.page + 1}
                                    itemsCountPerPage={pageRequest.size}
                                    totalItemsCount={totalItems}
                                    pageRangeDisplayed={10}
                                    onChange={handlePageChange}
                                    itemClass="page-item"
                                    linkClass="page-link"
                                />
                                <div className="dropdown m-2 mt-0">
                                    <select
                                        value={pageRequest.size}
                                        className="form-select"
                                        onChange={handlePageSizeChange}
                                    >
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                    </select>
                                </div>
                            </Col>
                        </Row>
                        
                        {/* Display Range */}
                        <Row>
                            <Col>
                                <div className="d-flex mt-2">
                                    <div style={{ fontFamily: 'Bolt', color: 'blue' }}>
                                        Displaying: {displayRange} of {totalItems}
                                    </div>
                                    <div style={{ marginLeft: '20px', fontFamily: 'Bolt', color: 'blue' }}>
                                        Total Pages: {totalPages}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            )}
        </Container>
    );
};

export default SoldHistory;
